import React, { useEffect, useState } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  Radio,
  Stack,
  Typography,
  Switch,
  useMediaQuery,
  Tooltip,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

import { IApp, ILauncherContext } from 'types';
import { styled } from '@mui/system';
import { red, grey } from '@mui/material/colors';
import { DARK } from 'lib/config';
import { useTranslation } from 'react-i18next';
import { useInitProvider } from 'providers/InitProvider';
import { useNotificationsContext } from 'providers/NotificationsProvider';
import { format } from 'date-fns';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CircleIcon from '@mui/icons-material/Circle';
import ButtonLabel from 'components/profile/ButtonLabel';
import { useNotificationsMarkAsRead } from 'lib/useQueryNotifications';
import Colors from 'components/Header/utils/colors';
import { log } from 'console';

const MAX_LENGTH = 135;
const truncate = (input) => input.length > MAX_LENGTH ? <Tooltip title={input}><div>{`${input.substring(0, MAX_LENGTH)}...`}</div></Tooltip> : input;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  '.homelink': {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '.MuiTypography-root': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
}));

const WrapperInt = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 10px',
}));

const WrapperIntMobile = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 5px',
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
}))

const NotificationsList = styled('div')(({ theme }) => ({
  height: '100%',
  maxHeight: '100%',
  minHeight: '400px',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '0 8px'
}))

const NotificationsItem = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  padding: '12px 16px',
  borderBottom: `1px solid ${Colors.notificationsCardBorder}`,
  background: Colors.white,
  borderRadius: '8px',
  marginBottom: '8px',

  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    background: Colors.notificationsHoverBg,

    '.MuiRadio-root': {
      opacity: 1
    }
  },
}))

const ExtLink = styled('a')(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  transition: 'all ease 0.25s',
  fontSize: '12px',
  display: 'block',

  // '&:hover': {
  //   color: theme.palette.primary.main,
  //   textDecoration: 'none',
  //   background: '#eaeaea',

  //   '.MuiRadio-root': {
  //     opacity: 1
  //   }
  // },
}));

const NotificationsItemFlex = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
}))

const NotificationsItemImg = styled('img')(({ theme }) => ({
  width: '20px',
  position: 'relative',
  bottom: '-2px'
}))

const NotificationsItemFlexFull = styled('div')(({ theme }) => ({
  width: 'calc(100% - 28px)'
}))

const NotificationsItemContent = styled('div')(({ theme }) => ({
  paddingRight: '40px',
  marginTop: '4px',
}))

const NotificationsRadio = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: '0',
  top: '25px',
  zIndex: 100
}))

const NotificationsToReadFlex = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '@media (max-width: 780px)': {
    gap: '4px',
  }
}))

const NotificationsTabs = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 8px 12px 8px',
  '@media (max-width: 780px)': {
    gap: '4px',
  }
}))

const NotificationsEmpty = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '40px 20px',
  textAlign: 'center',
}))

const ButtonStandard = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontFamily: 'WotfardRegular',
  borderWidth: '1px !important',
}))

const ButtonOutline = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontFamily: 'WotfardRegular',
  borderWidth: '1px !important',
  borderColor: Colors.lightGrey + ' !important',
  '&:hover': {
    borderColor: theme.palette.primary.main + ' !important',
  }
}))

const searchApp = (dump: any, appName: string) => {
  return dump.apps.findIndex((app) => app.code === appName) === -1
    ? false
    : true;
};

const DEFAULT_APP_LOGO = './assets/images/icon.png';

const NotificationsView = ({
  data,
  type,
  gotoHomeHandler,
  logoHandler,
  defaultApp,
  workspaceSelected,
}: {
  data: ILauncherContext;
  type: string;
  defaultApp: IApp;
  logoHandler: (data: IApp) => void;
  gotoHomeHandler?: () => void;
  workspaceSelected: boolean;
}) => {

  const [showToRead, setShowToRead] = useState(false)
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const { dump, amIUsingTheDefaultApp } = useInitProvider();
  const { notifications, counter, refetch } = useNotificationsContext();

  const mutation = useNotificationsMarkAsRead()

  const accountExists = searchApp(dump, 'account');
  const { available, current } = dump?.workspace;
  const currentWorkspace = available.find((item) => item.code === current);
  const currentWorkspaceName = currentWorkspace ? currentWorkspace.name : available.length > 0 ? available[0].name : null;

  const matches = useMediaQuery('(max-width:599px)');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setShowToRead(false);
    }, 500);
  };

  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  const openMore = Boolean(anchorElMore);
  const handleClickMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  const handleCloseMore = () => {
    setAnchorElMore(null);
  };

  const handleMarkAsRead = async (id: string) => {
    await mutation.mutateAsync({ notificationId: id } as any);
    console.log(` +++ mutate handleMarkAsRead refetch()`)
    refetch();
  }

  const handleMarkAllAsRead = async () => {
    await mutation.mutateAsync({ notificationId: '*' } as any);
    console.log(` +++ mutate handleMarkAllAsRead refetch()`)
    refetch();
  }

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'launcher-popover' : undefined;
  const { t } = useTranslation();
  if (workspaceSelected) {
    return <></>;
  }

  const WrapperComp = matches ? WrapperIntMobile : WrapperInt;

  const safeNotifications = notifications || [];
  const notificationsToRead = safeNotifications.filter(el => el.state === 0)
  const notificationsToShow = showToRead ? safeNotifications.filter(el => el.state === 0) : safeNotifications;
  const noNotifications = !notifications || (notifications && notifications.length === 0);

  const renderNotifications = () => {
    if (!notificationsToShow || (notificationsToShow && notificationsToShow.length === 0)) {
      return <NotificationsEmpty>
        {/* <img src="/assets/images/empty-notifications.jpg" alt="No notifications" style={{ width: '200px', marginLeft: '-10px' }} /> */}
        <Typography variant="h6" fontWeight="bold">{t('noNotifications', 'All notifications have been read')}</Typography>
      </NotificationsEmpty>
    }
    return notificationsToShow.map(n => {
      const notificationApp = dump.apps.filter(el => el.code === n.appCode)[0]
      // console.log(`notificationsToShow.map notificationApp "${n.appCode}" ${JSON.stringify(notificationApp)}`)
      const notificationAppLogo = notificationApp ? notificationApp.icon || DEFAULT_APP_LOGO : DEFAULT_APP_LOGO;
      const notificationAppName = notificationApp ? notificationApp.label.replace('Requiro ', '') : t('notificationTitle', 'Notification')

      let notificationUrl = '#';
      if (!n.url) {
        notificationUrl = notificationApp
          ? notificationApp.location === 'shell'
            ? `/index.html#${notificationApp.code}`
            : notificationApp.uri
          : '#';
      } else {
        notificationUrl = n.url;
      }

      return <NotificationsItem key={n.notificationId} sx={{
        background: n.state === 0 ? Colors.notificationsToReadBg : Colors.white
      }}>
        <div>
          <NotificationsItemImg src={notificationAppLogo} alt="" />
        </div>
        <NotificationsItemFlexFull>
          <NotificationsItemFlex>
            <ExtLink href={notificationUrl} target='_blank'>
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '16px', lineHeight: '24px' }}>{notificationAppName}</Typography>
            </ExtLink>
            <Typography variant="body2" sx={{ fontWeight: '300', fontSize: '13px', lineHeight: '13px', marginTop: '6px' }}>{format(new Date(n.date), 'dd/MM/yyyy')}</Typography>
            {n.state === 0 &&
              <NotificationsRadio>
                <Tooltip
                  placement='left'
                  enterDelay={500}
                  title={t('notificationsMarkAsRead', 'Mark as read')}
                  arrow={false}
                  componentsProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: { offset: [-2, 0] },
                        },
                      ],
                    },
                  }}
                >
                  <IconButton
                    disabled={noNotifications}
                    aria-describedby={id}
                    onClick={() => {
                      handleMarkAsRead(n.notificationId)
                    }}
                    sx={{ border: 0, background: 'transparent', padding: '4px', '&:hover': { background: Colors.white } }}
                  >
                    <CircleIcon sx={{ color: '#1A416B', height: '8px', width: '8px' }} />
                  </IconButton>
                </Tooltip>
              </NotificationsRadio>
            }

          </NotificationsItemFlex>
          <NotificationsItemContent>
            <ExtLink href={notificationUrl} target='_blank'>
              {truncate(n.content)}
            </ExtLink>
          </NotificationsItemContent>
        </NotificationsItemFlexFull>
      </NotificationsItem>
    })
  }

  return (
    <>
      <WrapperComp>
        <Badge badgeContent={counter ? counter.toString() : null}>
          <IconButton
            aria-describedby={id}
            onClick={handleClick}
            sx={{ border: 0, background: open ? '#fff' : 'transparent' }}
          >
            {notificationsToRead.length > 0 ?
              <NotificationsIcon sx={{ color: open ? '#1A416B' : type === DARK ? grey[50] : red }} /> :
              <NotificationsOutlinedIcon sx={{ color: open ? '#1A416B' : type === DARK ? grey[50] : red }} />
            }
          </IconButton>
        </Badge>
      </WrapperComp>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          padding: 0,
          '& .MuiPopover-paper': {
            height: '80%'
          }
        }}
      >
        <Wrapper direction="column" spacing={0} sx={{
          padding: 0,
          width: '540px',
          height: '100%',
          '@media (max-width: 780px)': {
            width: '100%'
          }
        }}>
          <Header>
            <div>
              <Typography variant="body2">
                {accountExists && currentWorkspaceName && `Workspace ${currentWorkspaceName}`}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {t('notificationsCenter', 'Notifications center')}
              </Typography>
            </div>
            <NotificationsToReadFlex>
              <IconButton
                disabled={noNotifications}
                aria-describedby={id}
                onClick={handleClickMore}
                sx={{ border: 0, background: open ? '#fff' : 'transparent' }}
              >
                <MoreVertIcon
                  sx={{ color: '#1A416B' }}
                />
              </IconButton>
              <Popover
                id={id}
                open={openMore}
                anchorEl={anchorElMore}
                onClose={handleCloseMore}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  padding: 0
                }}
              >

                <Stack direction="column" sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <ButtonLabel
                      label={<strong>{t('notificationsMarkAllAsRead', 'Mark all as read')}</strong>}
                      onClick={() =>
                        handleMarkAllAsRead()
                      }
                    />
                  </Stack>
                </Stack>
              </Popover>
            </NotificationsToReadFlex>
          </Header>
          <NotificationsTabs>
            <ButtonStandard color="primary" variant={showToRead ? "outlined" : "contained"} style={{ flex: 1 }} onClick={() => { setShowToRead(false) }}>{t('notificationsAll', 'All')}</ButtonStandard>
            <ButtonOutline color="primary" variant={showToRead ? "contained" : "outlined"} style={{ flex: 1 }} onClick={() => { setShowToRead(true) }}>{t('notificationsToRead', 'To Read')}</ButtonOutline>
          </NotificationsTabs>
          <NotificationsList>
            {renderNotifications()}
          </NotificationsList>
        </Wrapper>
      </Popover>
    </>
  );
};

export default NotificationsView;
